import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveIcon, BoxesIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, SlidersHorizontalIcon, TrendingUpIcon } from 'lucide-react';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { RecoveryRules, defaultRecoveryRules, rulesMatchDefaults, summarizeRules } from '../data/recoveryRules';
import { SubmissionRules } from './SubmissionRules';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
import { UnitsDashboard } from './UnitsDashboard';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
type Batch = {
  arrived: string;
  name: string;
  received: number;
  msrp?: number;
  estimatedRecovery?: number;
  lineItems?: number;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const onePeopleIncomingBatches = [{
  arrived: 'Feb 5, 2026',
  name: 'Spring overstock',
  expected: 1365,
  received: 1267,
  progress: 92.8
}, {
  arrived: 'Dec 6, 2025',
  name: 'Assembly part 4',
  expected: 7,
  received: 7,
  progress: 100
}, {
  arrived: 'Aug 30, 2025',
  name: 'August returns',
  expected: 337,
  received: 335,
  progress: 99.4
}, {
  arrived: 'Jul 10, 2025',
  name: 'July overstock',
  expected: 16,
  received: 16,
  progress: 100
}, {
  arrived: 'May 2, 2025',
  name: 'May overstock',
  expected: 258,
  received: 258,
  progress: 100
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Uploaded products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Units uploaded',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
const eddyIncomingBatches = [{
  arrived: 'Nov 4, 2026',
  name: 'Eddy — January 3PL transfer',
  expected: 85,
  received: 86,
  progress: 100
}, {
  arrived: 'Jan 28, 2026',
  name: 'Eddy Samples',
  expected: 0,
  received: 0,
  progress: 0
}, {
  arrived: 'Dec 16, 2025',
  name: 'Eddy — December 2025 3PL transfer',
  expected: 1103,
  received: 1105,
  progress: 100
}, {
  arrived: 'Nov 21, 2025',
  name: 'Eddy 3PL transfer',
  expected: 118,
  received: 120,
  progress: 100
}, {
  arrived: 'Oct 10, 2025',
  name: 'Eddy 3PL Transfer',
  expected: 87,
  received: 86,
  progress: 100
}, {
  arrived: 'May 22, 2025',
  name: 'Eddy — TJX Overflow',
  expected: 0,
  received: 223,
  progress: 97.4
}, {
  arrived: 'May 19, 2025',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 845,
  progress: 99.7
}, {
  arrived: 'Sep 30, 2024',
  name: 'Eddy — October 3PL Transfer',
  expected: 22,
  received: 25,
  progress: 100
}, {
  arrived: 'Aug 13, 2024',
  name: 'Eddy — Fall 2024',
  expected: 168,
  received: 169,
  progress: 100
}, {
  arrived: 'Apr 8, 2024',
  name: 'Eddy April 2024',
  expected: 0,
  received: 0,
  progress: 100
}, {
  arrived: 'Jan 18, 2024',
  name: 'Eddy — Jan 2024',
  expected: 5,
  received: 0,
  progress: 100
}, {
  arrived: 'Dec 20, 1905',
  name: 'Eddy — 3PL transfer old',
  expected: 0,
  received: 183,
  progress: 100
}, {
  arrived: '—',
  name: 'Eddy — June TRX',
  expected: 139,
  received: 139,
  progress: 98.1
}, {
  arrived: '—',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 0,
  progress: 0
}];
type BrandBase = {
  receivedProducts: number;
  receivedUnits: number;
  totalMsrp: number;
  maxOpportunity: number;
  soldUnits: number;
  totalSales: number;
};
type BatchRow = Batch & {
  brand: string;
  id: string;
};
type SortKey = 'uploaded' | 'batch' | 'brand' | 'units' | 'msrp' | 'recovery';
const ROWS_PER_PAGE = 50;

// Submission dates arrive as display strings ("Feb 5, 2026") and can be missing.
function uploadedTime(value: string) {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
const onePeopleBase: BrandBase = {
  receivedProducts: 189,
  receivedUnits: 1902,
  totalMsrp: 130993,
  maxOpportunity: 65437,
  soldUnits: 2920,
  totalSales: 49411
};
const eddyBase: BrandBase = {
  receivedProducts: 320,
  receivedUnits: 2981,
  totalMsrp: 321656,
  maxOpportunity: 160828,
  soldUnits: 4010,
  totalSales: 325875
};
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const allBrands = selectedOwner === '';
  const includes = (brand: string) => allBrands || selectedOwner === brand;
  const nisoloActive = includes('Nisolo');
  const {
    inventory: nisolo
  } = useNisoloProducts(nisoloActive);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(nisoloActive);
  const {
    data: nisoloOrders
  } = useNisoloOrders(nisoloActive);
  const [section, setSection] = useState<'Submissions' | 'Units'>('Submissions');
  const [deselected, setDeselected] = useState<string[]>([]);
  const [openRules, setOpenRules] = useState<string | null>(null);
  const [rulesById, setRulesById] = useState<Record<string, RecoveryRules>>({});
  const rulesFor = (id: string) => rulesById[id] ?? defaultRecoveryRules;
  const setRulesFor = (id: string, rules: RecoveryRules) => setRulesById(current => ({
    ...current,
    [id]: rules
  }));
  const resetRulesFor = (id: string) => setRulesById(current => {
    const {
      [id]: _removed,
      ...rest
    } = current;
    return rest;
  });
  const [sort, setSort] = useState<{
    key: SortKey;
    direction: 'asc' | 'desc';
  }>({
    key: 'uploaded',
    direction: 'desc'
  });
  const [page, setPage] = useState(1);
  useEffect(() => setDeselected([]), [selectedOwner]);
  useEffect(() => setPage(1), [selectedOwner, sort]);
  const toggleSort = (key: SortKey) => setSort(current => current.key === key ? {
    key,
    direction: current.direction === 'asc' ? 'desc' : 'asc'
  } : {
    key,
    direction: key === 'batch' || key === 'brand' ? 'asc' : 'desc'
  });
  const brands = useMemo(() => {
    const entries: {
      brand: string;
      batches: Batch[];
      base: BrandBase;
    }[] = [];
    if (includes('1 People')) entries.push({
      brand: '1 People',
      batches: onePeopleIncomingBatches,
      base: onePeopleBase
    });
    if (includes('Eddy')) entries.push({
      brand: 'Eddy',
      batches: eddyIncomingBatches,
      base: eddyBase
    });
    if (nisoloActive) {
      entries.push({
        brand: 'Nisolo',
        batches: nisoloSubmissions?.submissions ?? [],
        base: {
          receivedProducts: nisolo?.totalProducts ?? 0,
          receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
          totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
          maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
          soldUnits: nisoloOrders?.units ?? 0,
          totalSales: nisoloOrders?.sales ?? 0
        }
      });
    }
    return entries;
  }, [selectedOwner, nisolo, nisoloSubmissions, nisoloOrders]);
  const unsortedRows: BatchRow[] = useMemo(
  // Batch names repeat within a brand, so the index keeps each row's id unique.
  () => brands.flatMap(({
    brand,
    batches
  }) => batches.map((batch, index) => ({
    ...batch,
    brand,
    id: `${brand}::${index}::${batch.name}`
  }))), [brands]);
  const rows: BatchRow[] = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const value = (row: BatchRow) => {
      switch (sort.key) {
        case 'uploaded':
          return uploadedTime(row.arrived);
        case 'batch':
          return row.name.toLocaleLowerCase();
        case 'brand':
          return row.brand.toLocaleLowerCase();
        case 'units':
          return row.received;
        case 'msrp':
          return row.msrp ?? 0;
        case 'recovery':
          return row.estimatedRecovery ?? 0;
      }
    };
    return [...unsortedRows].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      if (typeof left === 'string' || typeof right === 'string') return String(left).localeCompare(String(right)) * direction;
      return (left - right) * direction;
    });
  }, [unsortedRows, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);
  const isSelected = (row: BatchRow) => !deselected.includes(row.id);
  const toggleBatch = (row: BatchRow) => setDeselected(current => current.includes(row.id) ? current.filter(id => id !== row.id) : [...current, row.id]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? rows.map(row => row.id) : []);

  // Each brand contributes only its selected submissions. Brands whose batch
  // records carry no valuation fall back to a share of their known totals.
  const totals = useMemo(() => {
    return brands.reduce((sum, {
      brand,
      batches,
      base
    }) => {
      const selected = batches.filter((batch, index) => !deselected.includes(`${brand}::${index}::${batch.name}`));
      const brandUnits = batches.reduce((count, batch) => count + batch.received, 0);
      const selectedUnits = selected.reduce((count, batch) => count + batch.received, 0);
      const share = batches.length === 0 ? 1 : brandUnits > 0 ? selectedUnits / brandUnits : selected.length / batches.length;
      const scale = (value: number) => Math.round(value * share);
      const lines = selected.reduce((count, batch) => count + (batch.lineItems ?? 0), 0);
      const batchMsrp = selected.reduce((value, batch) => value + (batch.msrp ?? 0), 0);
      const batchRecovery = selected.reduce((value, batch) => value + (batch.estimatedRecovery ?? 0), 0);
      const matched = brand === 'Nisolo' ? selected.reduce((result, submission) => {
        const outcome = reconcileSubmission(submission, nisoloOrders);
        return {
          units: result.units + outcome.soldUnits,
          sales: result.sales + outcome.actualSales
        };
      }, {
        units: 0,
        sales: 0
      }) : {
        units: scale(base.soldUnits),
        sales: scale(base.totalSales)
      };
      return {
        products: sum.products + (lines > 0 ? lines : scale(base.receivedProducts)),
        lines: sum.lines + lines,
        units: sum.units + (batches.length > 0 ? selectedUnits : base.receivedUnits),
        msrp: sum.msrp + (batchMsrp > 0 ? batchMsrp : scale(base.totalMsrp)),
        recovery: sum.recovery + (batchRecovery > 0 ? batchRecovery : scale(base.maxOpportunity)),
        soldUnits: sum.soldUnits + matched.units,
        totalSales: sum.totalSales + matched.sales
      };
    }, {
      products: 0,
      lines: 0,
      units: 0,
      msrp: 0,
      recovery: 0,
      soldUnits: 0,
      totalSales: 0
    });
  }, [brands, deselected, nisoloOrders]);
  const receivedProducts = totals.products;
  const receivedUnits = totals.units;
  const selectedLines = totals.lines;
  const predictedRecovery = totals.recovery;
  const soldUnits = totals.soldUnits;
  const totalSales = totals.totalSales;
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }, {
    label: 'Units sold',
    value: soldUnits.toLocaleString('en-US'),
    detail: 'Matched to selected uploads'
  }, {
    label: 'Total sales',
    value: currency(totalSales),
    detail: 'Gross recovery revenue'
  }, {
    label: 'Actual recovery / unit',
    value: perUnit(totalSales, soldUnits),
    detail: soldUnits > 0 ? 'Per unit sold' : 'No sales yet'
  }];
  const msrpValue = totals.msrp;
  const totalMsrp = currency(msrpValue);
  const maxOpportunity = currency(predictedRecovery);
  const totalReceivedValue = currency(msrpValue);
  const overstockDetail = `${receivedUnits.toLocaleString('en-US')} units across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-1069" data-mp="3cil3xpml0r2y">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-1070" data-mp="37kjaqbvfzbdr">
        <div data-id="element-1071" data-mp="85xtahbgb4ru">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-1072" data-mp="42835nf4of4p">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1073" data-mp="3vgdya3j53soq" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1074" data-mp="3qim1ow7jvie7">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-1075" data-mp="n13wyxn2qri0">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1076" data-mp="blqtn6tzlugz">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-1077" data-mp="3tsdn75frjnu9" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <div className="mt-6 flex gap-1 rounded-full border border-ink/10 bg-cream/60 p-1 w-fit" role="tablist" aria-label="Inventory sections" data-id="element-1078" data-mp="q926rd5thmj7">
        {(['Submissions', 'Units'] as const).map(tab => <button key={tab} type="button" role="tab" aria-selected={section === tab} onClick={() => setSection(tab)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${section === tab ? 'bg-[#BB5E40] text-cream shadow-sm' : 'text-ink/55 hover:text-ink'}`} data-id="element-1079" data-mp="gkagd9genaae">
            {tab}
          </button>)}
      </div>

      {section === 'Units' ? <div className="mt-6" data-id="element-1080" data-mp="1nbvjy4n4032u">
          <UnitsDashboard selectedOwner={selectedOwner} embedded data-id="element-1081" data-mp="1jcwjoxcbouhq" />
        </div> : <>
      <section className="mt-6 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" aria-label="Inventory overview" data-id="element-1082" data-mp="1x4b3v7hyv1qd">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-1083" data-mp="1s46o5jovub17">
          <div className="flex items-center gap-2" data-id="element-1084" data-mp="14fvt4x5vb5ku">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1085" data-mp="10dsbtrwip6rn" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-1086" data-mp="10fvbz4rdwga3">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-3" data-id="element-1087" data-mp="1eaxf5k4ujrj7">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 pl-4 ${index % 3 === 0 ? 'border-l-0 pl-0' : 'border-l-2'} ${index >= 3 ? 'border-t pt-5 sm:border-t-0 sm:pt-0' : ''}`} data-id="element-1088" data-mp="2ps6y85k2chjz" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-1089" data-mp="2subqr4373y31">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-1090" data-mp="2sw4mvpg6mzws">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-1091" data-mp="2wxhvu8eknj7x">{metric.detail}</p>
              </div>)}
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream shadow-sm sm:p-7" aria-labelledby="opportunity-heading" data-id="element-1092" data-mp="36u30utqlcubr">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-1093" data-mp="293znwn95t06u">
            <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-1094" data-mp="2e5xe61fz8v9a" />
            <h2 id="opportunity-heading" className="text-lg font-semibold text-cream" data-id="element-1095" data-mp="2j3qi0ntt5d6e">Recovery value</h2>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-1096" data-mp="2nw0e6llin9h4">
            <div data-id="element-1097" data-mp="1qfkw3xr0pi4z">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1098" data-mp="1qg34fsqyfmt9">Total MSRP</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1099" data-mp="1vdw74bt1tyos">{totalMsrp}</p>
            </div>
            <div data-id="element-1100" data-mp="1nu3nlo14wfw9">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-1101" data-mp="1nun0gqbxge3f">Max opportunity</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-1102" data-mp="1iwtvu1q9vs59">{maxOpportunity}</p>
            </div>
          </div>
          <p className="mt-5 border-t border-cream/15 pt-4 text-sm leading-relaxed text-cream/70" data-id="element-1103" data-mp="18y4j0nd8gxvv">Based on inventory that has been received and evaluated for recovery.</p>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.78fr_1.22fr]" data-id="element-1104" data-mp="1050zpyfwpqtb">
        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="overstock-heading" data-id="element-1105" data-mp="103pv5wen0f15">
          <div className="flex items-center gap-2" data-id="element-1106" data-mp="q7mvqyb66uhw">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1107" data-mp="q97pwmtkobf8" />
            <h2 id="overstock-heading" className="text-base font-semibold" data-id="element-1108" data-mp="l70atodg4f8o">Overstock</h2>
          </div>
          <p className="mt-5 text-sm text-ink/55" data-id="element-1109" data-mp="h7899vmgz9ct">Total MSRP value</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-ink" data-id="element-1110" data-mp="2534poy1gthgt">{totalReceivedValue}</p>
          <p className="mt-1 text-sm text-ink/55" data-id="element-1111" data-mp="2a1fugucwl1s6">{overstockDetail}</p>
          <div className="mt-6 rounded-xl bg-[#BB5E40]/5 p-4" data-id="element-1112" data-mp="1v5q82v3t2mmx">
            <div className="flex items-start gap-2" data-id="element-1113" data-mp="1v7b1uiiaq1jq">
              <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-1114" data-mp="2039wuoy4qht4" />
              <p className="text-sm leading-relaxed text-ink/65" data-id="element-1115" data-mp="1mbvcadokq8jy">Inventory moves into recovery once it is received, checked in, and matched to your current channel and pricing rules.</p>
            </div>
          </div>
        </article>

        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-1116" data-mp="1ccmbb0vajto3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-1117" data-mp="13e0tx91k2kow">
            <div className="flex items-center gap-2" data-id="element-1118" data-mp="17g5n66o3dfb0">
              <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1119" data-mp="17eui83iwu1ib" />
              <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-1120" data-mp="orvf2voc9a08">Inventory submissions</h2>
            </div>
            {pageCount > 1 && <nav className="flex items-center gap-1.5 text-[13px]" aria-label="Submission pages" data-id="element-1121" data-mp="xsjjqzrudy0g">
                <button type="button" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Previous page" data-id="element-1122" data-mp="ssffc847ktql">
                  <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1123" data-mp="1chpthhob36ml" />
                </button>
                <span className="tabular-nums text-ink/60" data-id="element-1124" data-mp="1qgyw1ejla283">
                  Page <span className="font-semibold text-ink" data-id="element-1125" data-mp="1lino59cuu1si">{currentPage}</span> of {pageCount}
                </span>
                <button type="button" onClick={() => setPage(Math.min(pageCount, currentPage + 1))} disabled={currentPage === pageCount} className="rounded-lg border border-ink/15 p-1 text-ink/60 transition-colors hover:border-ink/30 hover:text-ink disabled:opacity-35 disabled:hover:border-ink/15 disabled:hover:text-ink/60" aria-label="Next page" data-id="element-1126" data-mp="1xz1ddl69vy71">
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1127" data-mp="1ab03o8xcybq1" />
                </button>
              </nav>}
            <p className="ml-auto text-[13px] text-ink/50" data-id="element-1128" data-mp="1p0fufoww2h5q">{rows.length.toLocaleString('en-US')} submissions</p>
          </div>
          <div className="max-h-[420px] overflow-auto" data-id="element-1129" data-mp="1k6uv9tjk7h4y">
            <table className="w-full min-w-[880px] text-left text-sm" data-id="element-1130" data-mp="wibm7bsor0bt">
              <thead className="sticky top-0 z-10 bg-cream text-xs text-ink/50" data-id="element-1131" data-mp="rkieuffnc09y">
                <tr data-id="element-1132" data-mp="rifpzyq8cd6t">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-1133" data-mp="3373umbqxxikv">
                    <span className="flex items-center gap-2.5" data-id="element-1134" data-mp="37yc4f8w6n5yk">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-1135" data-mp="37zn8zaxgchqq" />
                      <SortButton label="Uploaded" sortKey="uploaded" sort={sort} onSort={toggleSort} data-id="element-1136" data-mp="2t7d04a2dr3ks" />
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1137" data-mp="3lxtya2oc59es"><SortButton label="Batch" sortKey="batch" sort={sort} onSort={toggleSort} data-id="element-1138" data-mp="3lw0ssqpgkv8d" /></th>
                  {allBrands && <th scope="col" className="px-3 py-3 font-semibold" data-id="element-1139" data-mp="3cxfbszzmxo9p"><SortButton label="Brand" sortKey="brand" sort={sort} onSort={toggleSort} data-id="element-1140" data-mp="3cwnf1luaxkhu" /></th>}
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1141" data-mp="3gxpd3zchfqi8"><SortButton label="Unit count" sortKey="units" sort={sort} onSort={toggleSort} align="right" data-id="element-1142" data-mp="21iiaaqm9l6lg" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1143" data-mp="25k3p4hiczuya"><SortButton label="MSRP value" sortKey="msrp" sort={sort} onSort={toggleSort} align="right" data-id="element-1144" data-mp="c14uo1yuuine" /></th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-1145" data-mp="72bchw8mi3gw"><SortButton label="Estimated recovery" sortKey="recovery" sort={sort} onSort={toggleSort} align="right" data-id="element-1146" data-mp="744ntou6lbv8" /></th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-1147" data-mp="3thw5269wul9b">Recovery rules</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-1148" data-mp="3phm1t1di68y5">
                {pageRows.length > 0 ? pageRows.map(row => {
                  const checked = isSelected(row);
                  const rules = rulesFor(row.id);
                  const custom = !rulesMatchDefaults(rules);
                  const expanded = openRules === row.id;
                  return <React.Fragment key={row.id} data-id="element-1149" data-mp="1dqqfo3n0etdq">
                    <tr className={checked ? '' : 'opacity-45'} data-id="element-1150" data-mp="1doe0fy8myx4d">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-1151" data-mp="18sgkvu575usn">
                        <span className="flex items-center gap-2.5" data-id="element-1152" data-mp="1h7ctbkvq4n0b">
                          <SelectBox checked={checked} onChange={() => toggleBatch(row)} label={`Include ${row.name} in totals`} data-id="element-1153" data-mp="1l9rb01nnpgq0" />
                          {row.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-1154" data-mp="1uyrcbrpmso7d">{row.name}</th>
                      {allBrands && <td className="whitespace-nowrap px-3 py-4 text-ink/60" data-id="element-1155" data-mp="yb4kk73i5u09">{row.brand}</td>}
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-1156" data-mp="12bwvd4s0w6yn">{row.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1157" data-mp="17ckuyeubhw4h">{row.msrp && row.msrp > 0 ? currency(row.msrp) : '—'}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-1158" data-mp="2iq6lo4fts5s4">{row.estimatedRecovery && row.estimatedRecovery > 0 ? currency(row.estimatedRecovery) : '—'}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-right sm:px-6" data-id="element-1159" data-mp="fiq11vtjqnq">
                        <button type="button" onClick={() => setOpenRules(expanded ? null : row.id)} aria-expanded={expanded} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${custom ? 'border-[#BB5E40] bg-[#BB5E40]/8 text-[#94452E]' : 'border-ink/15 text-ink/60 hover:border-[#BB5E40]/40 hover:text-ink'}`} data-id="element-1160" data-mp="3rmbtkce341vf">
                          <SlidersHorizontalIcon className="h-3 w-3" aria-hidden="true" data-id="element-1161" data-mp="s6f240a21r9y" />
                          {custom ? summarizeRules(rules) : 'Account defaults'}
                          <ChevronUpIcon className={`h-3 w-3 transition-transform ${expanded ? '' : 'rotate-180'}`} aria-hidden="true" data-id="element-1162" data-mp="126foxw74upiw" />
                        </button>
                      </td>
                    </tr>
                    {expanded && <tr data-id="element-1163" data-mp="1lr65yej5wtyn">
                        <td colSpan={allBrands ? 7 : 6} className="bg-cream/30 px-5 pb-5 sm:px-6" data-id="element-1164" data-mp="1lrfyo9vldf81">
                          <SubmissionRules batchName={row.name} rules={rules} onChange={next => setRulesFor(row.id, next)} onReset={() => resetRulesFor(row.id)} data-id="element-1165" data-mp="16uowxtyg3rf9" />
                        </td>
                      </tr>}
                    </React.Fragment>;
                }) : <tr data-id="element-1166" data-mp="o1kzdxi3wufd">
                    <td colSpan={allBrands ? 7 : 6} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-1167" data-mp="t28zr9s86nm9">No inventory submissions yet.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {nisoloActive && <section className="mt-5" aria-label="AI insights" data-id="element-1168" data-mp="2lqq8qib3uiyj">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter((submission, index) => !deselected.includes(`Nisolo::${index}::${submission.name}`))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-1169" data-mp="2lt2wjc3jnnji" />
        </section>}
        </>}
    </div>;
}
function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left'
}: {
  label: string;
  sortKey: SortKey;
  sort: {
    key: SortKey;
    direction: 'asc' | 'desc';
  };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return <button type="button" onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`} className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink ${active ? 'text-ink' : 'text-ink/50'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-1170" data-mp="2qquog646v9np">
      {label}
      <ChevronUpIcon className={`h-3 w-3 shrink-0 transition-transform ${active ? 'opacity-100' : 'opacity-0'} ${active && sort.direction === 'desc' ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1171" data-mp="3qzau0roofluu" />
    </button>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-1172" data-mp="djd8pk2sv43e">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1173" data-mp="rimdlnlk2bs2" />}
    </button>;
}